import { Cross, Ellipsis, FC, Flex, HoverIcon, spacer } from '@babushkaui/core';
import { Snackbar, SnackbarContent } from '@mui/material';
import { Exists } from '@orchestrahealth/typedash';
import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';

type SnackbarOptions = {
  message: string;
  color?: string;
  textColor?: string;
  icon?: ReactNode;
  anchorOrigin?: { vertical: 'top' | 'bottom'; horizontal: 'left' | 'center' | 'right' };
  autoHideDuration?: number;
  closeControl?: boolean;
};

type SnackbarContextType = {
  showSnackbar: (options: SnackbarOptions) => void;
};

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

export const SnackbarProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [queue, setQueue] = useState<SnackbarOptions[]>([]);
  const [current, setCurrent] = useState<SnackbarOptions | null>(null);
  const [open, setOpen] = useState(false);

  const showSnackbar = useCallback((opts: SnackbarOptions) => {
    setQueue((prev) => [
      ...prev,
      {
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
        ...opts,
      },
    ]);
  }, []);

  useEffect(() => {
    if (!current && queue.length > 0) {
      const next = queue[0];
      setQueue((prev) => prev.slice(1));
      setCurrent(next);
      setOpen(true);
    }
  }, [queue, current]);

  const handleClose = () => setOpen(false);

  const handleExited = () => {
    setCurrent(null);
  };

  const action = (
    <Exists value={current?.closeControl}>
      <HoverIcon Icon={Cross} color={current?.color} onClick={handleClose} />
    </Exists>
  );

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}
      <Snackbar
        key={current?.message}
        open={open}
        autoHideDuration={current?.autoHideDuration}
        anchorOrigin={current?.anchorOrigin}
        onClose={handleClose}
        TransitionProps={{ onExited: handleExited }}
      >
        <SnackbarContent
          sx={{
            backgroundColor: current?.color ?? 'black',
            color: current?.textColor ?? 'white',
            borderRadius: spacer(8),
          }}
          message={
            <Flex style={{ alignItems: 'center', gap: spacer(2) }}>
              {current?.icon}
              <Flex style={{ maxWidth: spacer(55) }}>
                <Ellipsis>{current?.message}</Ellipsis>
              </Flex>
            </Flex>
          }
          action={action}
        />
      </Snackbar>
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = () => {
  const ctx = useContext(SnackbarContext);
  if (!ctx) throw new Error('useSnackbar must be used within SnackbarProvider');
  return ctx;
};
